import React, { useEffect, useRef, useMemo } from 'react';
import {
  Render,
  RenderStyle,
  HistoryPlugins,
  MouseAction,
  IImage,
  createImage,
  createVideo,
  setShapePreset,
  SmartMask,
  PathBrush,
} from '@meitu/whee-infinite-canvas';
import useDisableWheelZoom from '$/hooks/useDisableWheelZoom';
import { getVideoCoverUrl } from '$/utils/upload';
import {
  setRender,
  setRenderStyle,
  setImageTarget,
  setImageTargets,
  setHistoryPlugins,
  handleFitScreen,
  setZoomNum,
  setHistoryStatus,
  setHasMask,
  setCurrentObjects,
  AI_TOOLS_MIN_ZOOM_PERCENT,
  AI_TOOLS_MAX_ZOOM_PERCENT,
} from '@/store/useAIToolsCanvasStore';
import { isHasMask } from '$/pages/RoboneoCanvas/components/InfiniteCanvas/ImagesTask';
import {
  createAIEraserManualMaskHistorySnapshot,
  isApplyingAIEraserManualMaskHistory,
  submitAIEraserManualMaskHistory,
  type AIEraserManualMaskHistorySnapshot,
} from '@/pages/--roboneo/ai_eraser/utils/canvasHistory';
import renderStyleOptions, { interactiveRenderStyleOptions } from './renderStyle';
import { bindHotKeys, unbindHotKeys } from './hotKey';
import style from './index.module.scss';

/**
 * 是否存在可用于「开始消除」的 mask。
 * 自动消除：父组件传入的 mask URL 已在 LeftPanel 经 checkMaskUrlCorrec 校验后才写入 store，
 * 因此此处约定「smartMaskUrls 非空」即视为存在自动消除 mask。
 */
function computeAIToolsHasMask(render: Render | null, smartMaskUrls: string[]): boolean {
  const hasAutoMask = smartMaskUrls.length > 0;
  const brush = render?._FC.freeDrawingBrush as PathBrush | undefined;
  if (!brush || typeof brush.exportContainerToCanvas !== 'function') {
    return hasAutoMask;
  }
  const hasManual = isHasMask(brush.exportContainerToCanvas(), brush.getMasks?.() as never);
  return hasAutoMask || hasManual;
}

export const CANVAS_CONTAINER_ID = 'ai_tools_canvas_box';

export interface MediaItem {
  url: string;
  type: 'image' | 'video';
  /** 视频封面图 URL */
  coverUrl?: string;
}

export interface AIToolsInfiniteCanvasProps {
  /** 单个媒体 URL（向后兼容，mediaItems 优先） */
  imageUrl?: string;
  /** 媒体类型（配合 imageUrl 使用），默认 image */
  mediaType?: 'image' | 'video';
  /** 多媒体列表（AI 抠图等多图/多视频场景，优先级高于 imageUrl） */
  mediaItems?: MediaItem[];
  /** 单图自动消除 mask 图 URL（在校验通过后传入，非空即有自动 mask） */
  maskImages?: string[];
  /** 是否允许画布内的元素进行点选、拖拽、操作 */
  interactiveObjects?: boolean;
  /** 固定文档尺寸场景：居中放置画布，由外层为 #ai_tools_canvas_box 设置具体宽高 */
  fixedWorkspace?: boolean;
  fixedWorkspaceWidth?: number;
  fixedWorkspaceHeight?: number;
  /** 是否显示对象选中态（描边、缩放与旋转手柄） */
  showSelectionControls?: boolean;
  /** 控制媒体变更后是否重新适应屏幕；不传时保持每次媒体变更都自适应的旧行为 */
  autoFitKey?: React.Key;
  /** 媒体对象已经创建并写入画布后触发，用于外层同步业务 loading 状态。 */
  onMediaLoaded?: (items: MediaItem[]) => void;
}

/** 锁定元素交互的公共配置 */
const LOCKED_OBJECT_OPTIONS = {
  selectable: false,
  lockMovementX: true,
  lockMovementY: true,
  lockRotation: true,
  lockScalingX: true,
  lockScalingY: true,
  hasControls: false,
  hasBorders: false,
} as const;

type CanvasViewportTransform = [number, number, number, number, number, number];

/**
 * 拷贝当前画布视口矩阵，避免媒体对象重建时丢失用户手动缩放。
 */
function cloneViewportTransform(render: Render): CanvasViewportTransform | null {
  const { viewportTransform } = render._FC;
  if (!viewportTransform) return null;
  return [...viewportTransform] as CanvasViewportTransform;
}

/**
 * 恢复用户切图前的视口矩阵，并通知依赖 viewport:zoom 的工具栏与视频控制条同步状态。
 */
function restoreViewportTransform(render: Render, viewportTransform: CanvasViewportTransform | null) {
  if (!viewportTransform) return;
  render._FC.setViewportTransform(viewportTransform);
  render._FC.renderAll();

  const zoom = render._FC.getZoom?.() ?? viewportTransform[0];
  if (Number.isNaN(zoom)) return;
  render._FC.fire('viewport:zoom', { zoom });
  setZoomNum(Math.round(zoom * 100));
}

/**
 * AI 工具页通用无限画布组件
 * - 图片通过 createImage 渲染为画布对象
 * - 视频通过 createVideo 渲染为画布对象
 * - 支持通过 mediaItems 同时展示多张图片/视频
 */
export default function AIToolsInfiniteCanvas({
  imageUrl,
  mediaType = 'image',
  mediaItems,
  maskImages,
  interactiveObjects = false,
  fixedWorkspace = false,
  fixedWorkspaceWidth,
  fixedWorkspaceHeight,
  showSelectionControls = false,
  autoFitKey,
  onMediaLoaded,
}: AIToolsInfiniteCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderRef = useRef<Render | null>(null);
  const hasLoadedMediaRef = useRef(false);
  const mediaLoadVersionRef = useRef(0);
  const lastAutoFitKeyRef = useRef<React.Key | undefined>(undefined);
  const onMediaLoadedRef = useRef(onMediaLoaded);
  const maskImagesRef = useRef(maskImages ?? []);
  const manualMaskSnapshotRef = useRef<AIEraserManualMaskHistorySnapshot>({ objects: [] });
  const manualMaskHistoryFrameRef = useRef<number>(0);
  maskImagesRef.current = maskImages ?? [];
  onMediaLoadedRef.current = onMediaLoaded;

  // 将 imageUrl + mediaType 归一化为 mediaItems 数组
  const normalizedItems: MediaItem[] = useMemo(() => {
    if (mediaItems?.length) return mediaItems;
    if (!imageUrl) return [];
    return [{ url: imageUrl, type: mediaType }];
  }, [mediaItems, imageUrl, mediaType]);

  // 禁用浏览器默认缩放
  useDisableWheelZoom();

  // 初始化画布
  useEffect(() => {
    const render = new Render({
      container: CANVAS_CONTAINER_ID,
      minZoom: AI_TOOLS_MIN_ZOOM_PERCENT / 100,
      maxZoom: AI_TOOLS_MAX_ZOOM_PERCENT / 100,
      showRailing: false,
      selectionKey: 'shiftKey',
      backgroundColor: 'rgba(0,0,0,0)',
    });

    renderRef.current = render;
    setRender(render);
    render._FC.set('backgroundColor', 'transparent');
    const syncActiveObjects = () => {
      setCurrentObjects(render._FC.getActiveObjects());
    };
    render._FC.on('selection:created', syncActiveObjects);
    render._FC.on('selection:updated', syncActiveObjects);
    render._FC.on('selection:cleared', () => {
      setCurrentObjects([]);
    });

    render._FC.on('object:added', (event) => {
      console.log('object:added', event.target);
    });

    const onMaskOrDelete = () => {
      const next = computeAIToolsHasMask(renderRef.current, maskImagesRef.current);
      console.log('onMaskOrDelete--hasMask', next);
      setHasMask(next);
    };

    render._FC.on('mask:path:created', onMaskOrDelete);
    render._FC.on('mask:path:deleted', onMaskOrDelete);
    render._FC.on('mask:rect:created', onMaskOrDelete);
    render._FC.on('mask:rect:deleted', onMaskOrDelete);

    /** 合并同一帧内的 mask 事件，并将手动画布变化写入历史栈。 */
    const scheduleManualMaskHistory = () => {
      if (isApplyingAIEraserManualMaskHistory()) return;
      if (manualMaskHistoryFrameRef.current) {
        window.cancelAnimationFrame(manualMaskHistoryFrameRef.current);
      }
      manualMaskHistoryFrameRef.current = window.requestAnimationFrame(() => {
        manualMaskHistoryFrameRef.current = 0;
        const before = manualMaskSnapshotRef.current;
        createAIEraserManualMaskHistorySnapshot(render)
          .then((after) => {
            if (isApplyingAIEraserManualMaskHistory()) return;
            submitAIEraserManualMaskHistory(before, after, (snapshot) => {
              manualMaskSnapshotRef.current = snapshot;
            });
            manualMaskSnapshotRef.current = after;
          })
          .catch(() => {});
      });
    };
    render._FC.on('mask:path:created', scheduleManualMaskHistory);
    render._FC.on('mask:path:deleted', scheduleManualMaskHistory);
    render._FC.on('mask:rect:created', scheduleManualMaskHistory);
    render._FC.on('mask:rect:deleted', scheduleManualMaskHistory);
    render._FC.on('mask:history:changed', scheduleManualMaskHistory);

    // 根据页面需求切换选中态样式：默认极简，按需开启编辑手柄与描边。
    const selectedRenderStyleOptions = showSelectionControls
      ? interactiveRenderStyleOptions
      : renderStyleOptions;
    const renderStyle = new RenderStyle(render, selectedRenderStyleOptions as any);
    setRenderStyle(renderStyle);

    // 监听画布缩放变化，更新 zoomNum 状态
    const handleZoomChange = (event: { zoom: number; }) => {
      if (Number.isNaN(event.zoom)) return;
      setZoomNum(Math.round(event.zoom * 100));
    };
    render._FC.on('viewport:zoom', handleZoomChange);
    // 监听历史记录改变事件
    render._FC.on('history:changed', () => {
      console.log('history:changed');
      setHistoryStatus(!!historyPlugins.history.undoStack.length, !!historyPlugins.history.redoStack.length);
    });
    // 异步加载资源，占位图颜色配置
    setShapePreset({
      imageBackgroundColor: '#fff',
      videoBackgroundColor: '#fff',
      gifBackgroundColor: '#fff',
    });

    // 鼠标交互（拖拽平移等）
    const mouseAction = new MouseAction(render);
    // 历史记录
    const historyPlugins = new HistoryPlugins(render);
    historyPlugins.__name__ = 'AIToolsHistoryPlugins';
    setHistoryPlugins(historyPlugins);

    render
      .use(renderStyle)
      .use(mouseAction)
      .use(historyPlugins);

    // 绑定快捷键
    bindHotKeys();

    return () => {
      console.log('infinite canvas unmount');
      unbindHotKeys();
      setRenderStyle(null);
      render._FC.off('viewport:zoom', handleZoomChange);
      render._FC.off('mask:path:created', onMaskOrDelete);
      render._FC.off('mask:path:deleted', onMaskOrDelete);
      render._FC.off('mask:rect:created', onMaskOrDelete);
      render._FC.off('mask:rect:deleted', onMaskOrDelete);
      render._FC.off('mask:path:created', scheduleManualMaskHistory);
      render._FC.off('mask:path:deleted', scheduleManualMaskHistory);
      render._FC.off('mask:rect:created', scheduleManualMaskHistory);
      render._FC.off('mask:rect:deleted', scheduleManualMaskHistory);
      render._FC.off('mask:history:changed', scheduleManualMaskHistory);
      if (manualMaskHistoryFrameRef.current) {
        window.cancelAnimationFrame(manualMaskHistoryFrameRef.current);
      }
      render._FC.off();
      setHistoryPlugins(null);
      setHistoryStatus(false, false);
      render.unmount();
      setRender(null);
      setImageTarget(null);
      setImageTargets([]);
      setCurrentObjects([]);
      renderRef.current = null;
    };
  }, [showSelectionControls]);

  // 当 normalizedItems 变更时，加载媒体到画布
  useEffect(() => {
    const render = renderRef.current;
    if (!render) return;
    const loadVersion = mediaLoadVersionRef.current + 1;
    mediaLoadVersionRef.current = loadVersion;

    if (!normalizedItems.length) {
      const oldObjects = render._FC.getObjects();
      if (oldObjects.length) {
        render._FC.remove(...oldObjects);
      }
      render._FC.renderAll();
      setCurrentObjects([]);
      setImageTarget(null);
      setImageTargets([]);
      hasLoadedMediaRef.current = false;
      lastAutoFitKeyRef.current = autoFitKey;
      return;
    }

    /**
     * 加载媒体到画布；同一 autoFitKey 下的结果/原图替换会保留用户当前缩放。
     */
    const loadMedia = async () => {
      const elements: IImage[] = [];
      const shouldAutoFit = autoFitKey === undefined
        || !hasLoadedMediaRef.current
        || lastAutoFitKeyRef.current !== autoFitKey;
      // 多图排列时的水平偏移量
      let offsetX = 0;
      const GAP = 20;

      for (let i = 0; i < normalizedItems.length; i += 1) {
        const item = normalizedItems[i];
        try {
          let el;
          const objectOptions = interactiveObjects ? {} : LOCKED_OBJECT_OPTIONS;
          if (item.type === 'video') {
            const cover = item.coverUrl || getVideoCoverUrl(item.url);
            // eslint-disable-next-line no-await-in-loop
            el = await createVideo('', {
              left: offsetX,
              top: 0,
              source: item.url,
              cover,
              frameType: 'video',
              ...objectOptions,
            });
          } else {
            // eslint-disable-next-line no-await-in-loop
            el = await createImage('', {
              left: offsetX,
              top: 0,
              src: item.url,
              ...objectOptions,
            });
          }

          elements.push(el as unknown as IImage);
          // 下一个元素的水平偏移 = 当前元素右边缘 + 间距
          offsetX += (el.width ?? 0) + GAP;
        } catch (error) {
          console.error(`Failed to load ${item.type} onto canvas:`, item.url, error);
        }
      }

      if (renderRef.current !== render || mediaLoadVersionRef.current !== loadVersion) return;

      const preservedViewportTransform = shouldAutoFit ? null : cloneViewportTransform(render);
      const oldObjects = render._FC.getObjects();
      if (oldObjects.length) {
        render._FC.remove(...oldObjects);
      }
      elements.forEach((el) => {
        render._FC.add(el);
      });
      render._FC.renderAll();
      setCurrentObjects([]);

      // 向后兼容：单图场景写入 imageTarget
      setImageTarget(elements[0] ?? null);
      setImageTargets(elements);
      manualMaskSnapshotRef.current = { objects: [] };

      if (shouldAutoFit) {
        handleFitScreen();
      } else {
        restoreViewportTransform(render, preservedViewportTransform);
      }
      hasLoadedMediaRef.current = true;
      lastAutoFitKeyRef.current = autoFitKey;
      onMediaLoadedRef.current?.(normalizedItems);
    };
    loadMedia();
  }, [normalizedItems, interactiveObjects, autoFitKey]);

  // 当 maskImages 变更时，将所有 mask 合并为一张后以 SmartMask 形式添加到当前笔刷
  useEffect(() => {
    if (!maskImages) return;

    const container = (renderRef.current?._FC.freeDrawingBrush as PathBrush | undefined)?.maskContainer;
    container?.getObjects().forEach((obj) => {
      if ((obj as any)?.maskType === 'smart') {
        container?.remove(obj);
      }
    });
    console.log('maskImages changed--进入', maskImages);
    if (!maskImages.length) {
      renderRef.current?._FC.renderAll();
      setHasMask(computeAIToolsHasMask(renderRef.current, maskImages));
      return;
    }

    const render = renderRef.current;
    if (!render) return;

    // 已去掉旧 smartMask、新图尚未挂上时，仍可根据 URL 认为存在自动 mask
    setHasMask(computeAIToolsHasMask(render, maskImages));

    const loadImg = (url: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

    (async () => {
      const imgs = await Promise.all(maskImages.map(loadImg));

      const width = Math.max(...imgs.map((i) => i.width));
      const height = Math.max(...imgs.map((i) => i.height));

      // 合并所有 mask（以 OR 方式叠加：任一 mask 白色区域均保留）
      // 不能直接 drawImage 叠加再统一处理：后叠的黑色区域会覆盖前面已合并的白色区域
      const mergeCanvas = document.createElement('canvas');
      mergeCanvas.width = width;
      mergeCanvas.height = height;
      const ctx = mergeCanvas.getContext('2d')!;

      // 初始化目标像素缓冲（全透明）
      const imageData = ctx.createImageData(width, height);
      const { data } = imageData;
      const [r, g, b] = [0xf7, 0x3e, 0x6c];

      // 逐张 mask 读取像素，白色区域（R>128）OR 写入目标 buffer
      imgs.forEach((img) => {
        const offscreen = document.createElement('canvas');
        offscreen.width = width;
        offscreen.height = height;
        const offCtx = offscreen.getContext('2d')!;
        offCtx.drawImage(img, 0, 0, width, height);
        const { data: srcData } = offCtx.getImageData(0, 0, width, height);

        for (let i = 0; i < srcData.length; i += 4) {
          if (srcData[i] > 128) {
            data[i] = r;
            data[i + 1] = g;
            data[i + 2] = b;
            data[i + 3] = 255;
          }
        }
      });

      ctx.putImageData(imageData, 0, 0);

      const mergedImg = new Image();
      mergedImg.src = mergeCanvas.toDataURL('image/png');
      mergedImg.onload = () => {
        const brush = render._FC.freeDrawingBrush as PathBrush | undefined;
        if (!brush || typeof (brush as any).addSmartMask !== 'function') {
          setHasMask(computeAIToolsHasMask(render, maskImages));
          return;
        }

        const smartMask = new SmartMask({
          img: mergedImg,
          revertImage: mergedImg,
          left: 0,
          top: 0,
          width,
          height,
        });
        (brush as any).addSmartMask(smartMask);
        render._FC.renderAll();
        setHasMask(computeAIToolsHasMask(render, maskImages));
      };
    })().catch(() => {
      setHasMask(computeAIToolsHasMask(renderRef.current, maskImages));
    });
  }, [maskImages]);

  return (
    <div
      ref={containerRef}
      className={style.infinite_canvas_container}
    >
      <div id={CANVAS_CONTAINER_ID} className={style.infinite_canvas_box} />
    </div>
  );
}
